/**
 * Workflow admin (status sets/transitions/priority schemes) + automation rules v1 — T-030 (tasks/W3-CONTRACTS.md §4)
 * self-contained (ไม่ import จาก ./index.ts) — กัน circular TDZ (ดูบทเรียนใน handoff.ts) และกันชนชื่อตอน `export *`
 * ⚠️ ชื่อ `Transition` ชนกับ body ของ `POST /tickets/:id/transition` ที่ index.ts มีอยู่แล้ว (ticket action ไม่ใช่ workflow entity)
 *    → entity ของงานนี้ใช้ชื่อ `StatusTransition`/`StatusTransitionInput` แทน (ตัดสินใจเอง — จดใน PR)
 */
import { z } from "zod";

const I18n = z.record(z.string(), z.string());
/** สำเนาเฉพาะที่ของ status type — ไม่ import `StatusType` จาก ./index.ts เพื่อกัน circular import ระหว่างสอง exports */
const WorkflowStatusType = z.enum(["open", "pending_customer", "pending_internal", "resolved", "closed"]);
export type WorkflowStatusType = z.infer<typeof WorkflowStatusType>;

// ---------------------------------------------------------------------------
// Status sets / statuses / transitions
// ---------------------------------------------------------------------------

export const OwnerType = z.enum(["global", "service", "team"]);
export type OwnerType = z.infer<typeof OwnerType>;

export const Status = z.object({
  id: z.uuid(),
  status_set_id: z.uuid(),
  code: z.string(),
  name: I18n,
  customer_label: I18n.nullable().optional(),
  color: z.string(),
  type: WorkflowStatusType,
  kanban_order: z.number().int(),
  is_initial: z.boolean(),
  wip_limit: z.number().int().nullable().optional(),
  auto_reopen_on_customer_reply: z.boolean(),
  notify_customer: z.boolean(),
});
export type Status = z.infer<typeof Status>;

export const StatusInput = z.strictObject({
  code: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, "ตัวพิมพ์เล็ก ตัวเลข underscore เท่านั้น"),
  name: I18n,
  customer_label: I18n.optional(),
  color: z.string().trim().min(1).max(32).optional(),
  type: WorkflowStatusType,
  kanban_order: z.number().int().optional(),
  is_initial: z.boolean().optional(),
  wip_limit: z.number().int().positive().nullable().optional(),
  auto_reopen_on_customer_reply: z.boolean().optional(),
  notify_customer: z.boolean().optional(),
});
export type StatusInput = z.infer<typeof StatusInput>;

export const StatusPatch = StatusInput.partial();
export type StatusPatch = z.infer<typeof StatusPatch>;

export const TRANSITION_ROLES = ["member", "team_lead", "dept_head", "admin", "customer", "automation", "api"] as const;
export const TransitionRole = z.enum(TRANSITION_ROLES);
export type TransitionRole = z.infer<typeof TransitionRole>;

/**
 * [W7 — scaffold] กติกาผู้อนุมัติของ transition (D-29/P7) — เป็น "config" เท่านั้น ไม่มี execute payload
 * approvers.ids = uuid ของ user/team แล้วแต่ kind · expires_hours ≤ 720 (default 72 ที่ request time) · allow_self default false
 */
export const ApprovalRule = z.strictObject({
  approvers: z.strictObject({
    kind: z.enum(["users", "team", "dept_head"]),
    ids: z.array(z.uuid()).min(1),
  }),
  min_approvals: z.number().int().min(1),
  expires_hours: z.number().int().min(1).max(720).optional(),
  allow_self: z.boolean().optional(),
});
export type ApprovalRule = z.infer<typeof ApprovalRule>;

export const StatusTransition = z.object({
  id: z.uuid(),
  status_set_id: z.uuid(),
  from_status_id: z.uuid().nullable(),
  to_status_id: z.uuid(),
  label: I18n.nullable().optional(),
  allowed_roles: z.array(TransitionRole),
  required_fields: z.array(z.string()),
  require_children_closed: z.boolean(),
  require_note: z.boolean(),
  ui_order: z.number().int(),
  is_primary: z.boolean(),
  /** [W7 — scaffold] ต้องอนุมัติก่อนเปลี่ยน (default false — ไม่มี transition เดิมไหนถูก gate) */
  requires_approval: z.boolean().optional(),
  /** [W7 — scaffold] กติกาผู้อนุมัติ — ใช้เมื่อ requires_approval=true เท่านั้น */
  approval_rule: ApprovalRule.nullable().optional(),
});
export type StatusTransition = z.infer<typeof StatusTransition>;

export const StatusTransitionInput = z.strictObject({
  from_status_id: z.uuid().nullable().optional(),
  to_status_id: z.uuid(),
  label: I18n.optional(),
  allowed_roles: z.array(TransitionRole).min(1),
  required_fields: z.array(z.string().max(128)).max(20).optional(),
  require_children_closed: z.boolean().optional(),
  require_note: z.boolean().optional(),
  ui_order: z.number().int().optional(),
  is_primary: z.boolean().optional(),
  /** [W7 — scaffold] เปิด gate การอนุมัติ — ห้ามชี้ target ที่ is_initial (422 ฝั่ง admin routes · L1) */
  requires_approval: z.boolean().optional(),
  approval_rule: ApprovalRule.nullable().optional(),
});
export type StatusTransitionInput = z.infer<typeof StatusTransitionInput>;

export const StatusTransitionPatch = StatusTransitionInput.partial();
export type StatusTransitionPatch = z.infer<typeof StatusTransitionPatch>;

export const StatusSet = z.object({
  id: z.uuid(),
  code: z.string(),
  name: I18n,
  owner_type: OwnerType,
  owner_id: z.uuid().nullable(),
  is_default: z.boolean(),
  reopen_closed_after_days: z.number().int(),
  /** [T-063 R4] ทีมงานตอบ public แล้วเปลี่ยนไปสถานะนี้เอง (รหัสสถานะในชุด · null = ไม่เปลี่ยน · ค่าเริ่มต้น "waiting_customer") */
  on_staff_public_reply: z.string().nullable(),
  statuses: z.array(Status).optional(),
  transitions: z.array(StatusTransition).optional(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type StatusSet = z.infer<typeof StatusSet>;

export const StatusSetInput = z.strictObject({
  code: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_-]+$/, "ตัวพิมพ์เล็ก ตัวเลข - _ เท่านั้น"),
  name: I18n,
  owner_type: OwnerType.optional(),
  owner_id: z.uuid().nullable().optional(),
  is_default: z.boolean().optional(),
  reopen_closed_after_days: z.number().int().min(0).max(3650).optional(),
  /** [T-063 R4] null = ปิดการเปลี่ยนอัตโนมัติ · รหัสสถานะต้องมีอยู่ในชุด (ตรวจตอนใช้งาน — ไม่พบ = ไม่เปลี่ยน) */
  on_staff_public_reply: z.string().max(64).nullable().optional(),
});
export type StatusSetInput = z.infer<typeof StatusSetInput>;

export const StatusSetPatch = StatusSetInput.omit({ code: true }).partial();
export type StatusSetPatch = z.infer<typeof StatusSetPatch>;

// ---------------------------------------------------------------------------
// Priority schemes / priorities
// ---------------------------------------------------------------------------

export const Priority = z.object({
  id: z.uuid(),
  scheme_id: z.uuid(),
  code: z.string(),
  name: I18n,
  color: z.string(),
  rank: z.number().int(),
  is_default: z.boolean(),
  icon: z.string().nullable().optional(),
});
export type Priority = z.infer<typeof Priority>;

export const PriorityInput = z.strictObject({
  code: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, "ตัวพิมพ์เล็ก ตัวเลข underscore เท่านั้น"),
  name: I18n,
  color: z.string().trim().min(1).max(32).optional(),
  rank: z.number().int(),
  is_default: z.boolean().optional(),
  icon: z.string().nullable().optional(),
});
export type PriorityInput = z.infer<typeof PriorityInput>;

export const PriorityPatch = PriorityInput.omit({ code: true }).partial();
export type PriorityPatch = z.infer<typeof PriorityPatch>;

export const PrioritySchemeOwnerType = z.enum(["global", "service"]);
export type PrioritySchemeOwnerType = z.infer<typeof PrioritySchemeOwnerType>;

export const PrioritySchemeFull = z.object({
  id: z.uuid(),
  name: I18n,
  owner_type: PrioritySchemeOwnerType,
  owner_id: z.uuid().nullable(),
  is_default: z.boolean(),
  priorities: z.array(Priority).optional(),
});
export type PrioritySchemeFull = z.infer<typeof PrioritySchemeFull>;

export const PrioritySchemeInput = z.strictObject({
  name: I18n,
  owner_type: PrioritySchemeOwnerType.optional(),
  owner_id: z.uuid().nullable().optional(),
  is_default: z.boolean().optional(),
});
export type PrioritySchemeInput = z.infer<typeof PrioritySchemeInput>;

export const PrioritySchemePatch = PrioritySchemeInput.partial();
export type PrioritySchemePatch = z.infer<typeof PrioritySchemePatch>;

// ---------------------------------------------------------------------------
// Automation rules v1 — tasks/W3-CONTRACTS.md §4
// ---------------------------------------------------------------------------

export const Trigger = z.enum([
  "ticket.created",
  "message.created",
  "ticket.status_changed",
  "ticket.holder_changed",
  "ticket.priority_changed",
  "ticket.tag_changed",
  "ticket.field_changed",
  "ticket.child_closed",
  "time.inactive",
  "time.unclaimed",
  "time.due_approaching",
  "time.overdue",
  "time.schedule",
  // [T-054] map จาก event จริง: csat_received (T-051) · ai_classified (T-052) — ดู engine.eventTypesForTrigger
  "ticket.csat_received",
  "ai.classified",
  // [W7 — scaffold] T-066 เติม mapping ใน engine (side conversation ได้รับตอบกลับ)
  "side_conversation.replied",
]);
export type Trigger = z.infer<typeof Trigger>;

export const FIXED_FIELDS = [
  "service",
  "channel",
  "status",
  "status_type",
  "priority",
  "holder_team",
  "holder_user",
  "tags",
  "requester_email",
  "requester_domain",
  "requester_kind",
  "subject",
  "body",
  "mailbox",
  "age_minutes",
  "message_count",
  "is_first_message",
  "previous_status",
  "inside_business_hours",
  "has_parent",
  "unclaimed_minutes",
  "inactive_minutes",
  // [T-030 — ตัดสินใจเอง จดใน PR] เพิ่มจากสเปคตั้งต้น: "message.created" มาจาก event message.public/message.internal จริงบน main
  // (ไม่ใช่ "message_added" ตามที่ W3-CONTRACTS §3 อธิบายไว้ — messages.ts ไม่ได้อยู่ในขอบเขตงานนี้ แก้ไม่ได้)
  // จึงไม่มีทางแยก "ลูกค้าตอบ" ออกจาก "staff ตอบ" ด้วย field เดิม — เพิ่ม field นี้เพื่อให้ seed rule #2 (05 §6)
  // ทำงานถูกต้อง (trigger เฉพาะตอนลูกค้าตอบ ไม่ใช่ staff) โดยไม่ต้องแก้ไฟล์นอกขอบเขต
  "message_direction",
  // [T-052 — W4-CONTRACTS §3] AI signal fields — ค่าจาก ai_runs.result ล่าสุดของ feature routing/sentiment
  // (helper latestAiResult ใน src/ai/results.ts) + tickets.language · confidence ใช้ได้กับ gt/lt
  "ai.category",
  "ai.confidence",
  "ai.sentiment",
  "ai.urgency",
  "language",
] as const;

export const Field = z.union([z.enum(FIXED_FIELDS), z.string().regex(/^custom_field:.+$/)]);
export type Field = z.infer<typeof Field>;

export const Op = z.enum(["eq", "neq", "in", "nin", "contains", "regex", "gt", "lt", "between", "is_empty", "is_not_empty"]);
export type Op = z.infer<typeof Op>;

export interface Condition {
  and?: Condition[];
  or?: Condition[];
  not?: Condition;
  field?: z.infer<typeof Field>;
  op?: z.infer<typeof Op>;
  value?: unknown;
}

export const Condition: z.ZodType<Condition> = z.lazy(() =>
  z.union([
    // {and:[]} = match เสมอ (ใช้จริงใน seed rules ข้อ 7 และเป็นค่าเริ่มต้นของ automation rule ที่ trigger คุมเงื่อนไขหมดแล้ว เช่น time.* บาง rule) — ห้าม .min(1)
    z.strictObject({ and: z.array(Condition) }),
    z.strictObject({ or: z.array(Condition) }),
    z.strictObject({ not: Condition }),
    z.strictObject({ field: Field, op: Op, value: z.unknown().optional() }),
  ]),
);

export const Action = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("set_status"), code: z.string().min(1) }),
  z.strictObject({ type: z.literal("set_priority"), code: z.string().min(1) }),
  z.strictObject({ type: z.literal("add_tags"), tags: z.array(z.string().min(1).max(64)).min(1) }),
  z.strictObject({ type: z.literal("remove_tags"), tags: z.array(z.string().min(1).max(64)).min(1) }),
  z.strictObject({ type: z.literal("set_field"), key: z.string().min(1), value: z.unknown() }),
  z.strictObject({ type: z.literal("set_due"), relative_minutes: z.number().int().optional(), at: z.iso.datetime().optional() }),
  z.strictObject({ type: z.literal("assign"), mode: z.enum(["specific", "round_robin", "unassign"]), user_id: z.uuid().optional() }),
  z.strictObject({
    type: z.literal("handoff"),
    to: z.strictObject({ team_id: z.uuid().optional(), user_id: z.uuid().optional(), department_id: z.uuid().optional() }),
    note: z.string().max(2000).optional(),
  }),
  z.strictObject({
    type: z.literal("notify"),
    to: z.strictObject({ users: z.array(z.uuid()).optional(), teams: z.array(z.uuid()).optional(), roles: z.array(z.enum(["holder", "team_lead", "dept_head"])).optional() }),
    template_key: z.string().optional(),
  }),
  z.strictObject({ type: z.literal("auto_reply"), template_id: z.uuid(), max_per_ticket: z.number().int().min(1).max(100).optional() }),
  z.strictObject({ type: z.literal("add_note"), template_id: z.uuid() }),
  z.strictObject({ type: z.literal("add_watcher"), user_id: z.uuid() }),
  z.strictObject({ type: z.literal("escalate") }),
  z.strictObject({ type: z.literal("webhook"), url: z.url() }),
  z.strictObject({ type: z.literal("move_to_triage") }),
  z.strictObject({ type: z.literal("stop") }),
]);
export type Action = z.infer<typeof Action>;

export const AutomationRuleScopeType = z.enum(["global", "service", "team"]);
export type AutomationRuleScopeType = z.infer<typeof AutomationRuleScopeType>;

export const AutomationRule = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string().nullable(),
  scope_type: AutomationRuleScopeType,
  scope_id: z.uuid().nullable(),
  enabled: z.boolean(),
  trigger: Trigger,
  trigger_config: z.record(z.string(), z.unknown()),
  conditions: Condition,
  actions: z.array(Action),
  priority: z.number().int(),
  stop_processing: z.boolean(),
  business_time: z.boolean(),
  version: z.number().int(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type AutomationRule = z.infer<typeof AutomationRule>;

export const AutomationRuleInput = z.strictObject({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  scope_type: AutomationRuleScopeType.optional(),
  scope_id: z.uuid().nullable().optional(),
  enabled: z.boolean().optional(),
  trigger: Trigger,
  trigger_config: z.record(z.string(), z.unknown()).optional(),
  conditions: Condition.optional(),
  actions: z.array(Action).min(1),
  priority: z.number().int().optional(),
  stop_processing: z.boolean().optional(),
  business_time: z.boolean().optional(),
});
export type AutomationRuleInput = z.infer<typeof AutomationRuleInput>;

export const AutomationRuleAdminPatch = AutomationRuleInput.partial();
export type AutomationRuleAdminPatch = z.infer<typeof AutomationRuleAdminPatch>;

export const AutomationRun = z.object({
  id: z.uuid(),
  rule_id: z.uuid(),
  rule_version: z.number().int(),
  ticket_id: z.uuid().nullable(),
  trigger: z.string(),
  matched: z.boolean(),
  dry_run: z.boolean(),
  reasons: z.array(z.unknown()),
  actions_result: z.array(z.unknown()),
  error: z.string().nullable(),
  started_at: z.iso.datetime(),
  duration_ms: z.number().int().nullable(),
});
export type AutomationRun = z.infer<typeof AutomationRun>;

export const AutomationRuleVersion = z.object({
  version: z.number().int(),
  snapshot: z.record(z.string(), z.unknown()),
  saved_by: z.uuid().nullable(),
  saved_at: z.iso.datetime(),
});
export type AutomationRuleVersion = z.infer<typeof AutomationRuleVersion>;

export const AutomationRollbackInput = z.strictObject({ version: z.number().int().min(1) });
export type AutomationRollbackInput = z.infer<typeof AutomationRollbackInput>;

export const AutomationTestInput = z.strictObject({ ticket_id: z.string().min(1) });
export type AutomationTestInput = z.infer<typeof AutomationTestInput>;
